import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  MenuItem,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';

import { HEADER_HEIGHT } from '@frontend/features/common/constants/layout';

import { ACTIVE_SEARCH_SUMMARY_HEIGHT } from './ActiveSearchSummary';

/**
 * Chip toggle de filtro, compartido por todos los buscadores.
 *
 * `count` es cuántos resultados quedarían si se activa: saber eso ANTES de
 * tocar evita el filtro que deja la lista vacía. Los que dan cero se muestran
 * deshabilitados, no se ocultan — si desaparecieran, la barra saltaría cada
 * vez que se filtra.
 */
export function FilterChip({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}) {
  const empty = count === 0 && !active;
  return (
    <Chip
      label={count === undefined ? label : `${label} (${count})`}
      size="small"
      color={active ? 'primary' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      disabled={empty}
      onClick={empty ? undefined : onClick}
    />
  );
}

/** Un grupo de filtros con su etiqueta; se apila en mobile. */
export function FilterGroup({
  label,
  stacked,
  children,
}: {
  label: string;
  stacked?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      display="flex"
      gap={0.75}
      alignItems={stacked ? 'flex-start' : 'center'}
      flexDirection={stacked ? 'column' : 'row'}
      flexWrap="wrap"
      width={stacked ? '100%' : undefined}
      py={stacked ? 1 : 0}
    >
      <Typography variant="caption" color="text.secondary" mr={stacked ? 0 : 0.5}>
        {label}
      </Typography>
      <Box display="flex" gap={0.75} flexWrap="wrap">
        {children}
      </Box>
    </Box>
  );
}

export interface SortOption {
  value: string;
  label: string;
}

interface Props {
  /** Los grupos de filtros; recibe `stacked` para la disposición de mobile. */
  children: (stacked: boolean) => ReactNode;
  /** Cuántos filtros hay puestos: en mobile es lo único visible del panel. */
  activeCount: number;
  onClear: () => void;
  /** Resultados visibles, para el botón de cerrar el panel. */
  resultCount: number;
  /**
   * Desde dónde se pega la barra. Por defecto queda bajo la barra de navegación
   * MÁS el resumen de la búsqueda activa, que es lo que hay encima en los
   * buscadores; una pantalla sin ese resumen —el listado de reservas— pasa su
   * propio tope, porque el default le dejaría una franja por la que las filas
   * pasarían de largo.
   */
  stickyTop?: number;
  sort?: {
    value: string;
    options: SortOption[];
    onChange: (value: string) => void;
  };
}

/**
 * Barra de filtros de resultados. En desktop queda FIJA bajo la barra de
 * navegación (las listas son largas y se refinan mirando resultados); en mobile
 * se reduce a un botón con el conteo de filtros activos que abre un panel
 * inferior, que es lo único usable con esta cantidad de opciones.
 */
export function ResultFiltersBar({
  children,
  activeCount,
  onClear,
  resultCount,
  stickyTop = HEADER_HEIGHT + ACTIVE_SEARCH_SUMMARY_HEIGHT,
  sort,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);

  const sortField = sort && (
    <TextField
      select
      size="small"
      sx={{ width: isMobile ? undefined : 170, flex: isMobile ? 1 : undefined, ml: isMobile ? 0 : 'auto' }}
      label={t('travelServices.cars.sort')}
      value={sort.value}
      onChange={(event) => sort.onChange(event.target.value)}
    >
      {sort.options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  if (isMobile) {
    return (
      <>
        <Box
          position="sticky"
          top={stickyTop}
          zIndex={(muiTheme) => muiTheme.zIndex.appBar - 1}
          display="flex"
          alignItems="center"
          gap={1}
          px={1.5}
          py={1}
          bgcolor="background.paper"
          borderBottom={1}
          borderColor="divider"
        >
          <Badge badgeContent={activeCount} color="primary">
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setOpen(true)}
            >
              {t('travelServices.cars.filters')}
            </Button>
          </Badge>
          {sortField}
        </Box>

        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2 },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1">
              {t('travelServices.cars.filters')}
            </Typography>
            <Button size="small" onClick={onClear}>
              {t('travelServices.cars.clearFilters')}
            </Button>
          </Box>
          {children(true)}
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => setOpen(false)}
          >
            {t('travelServices.showFilteredResults', { count: resultCount })}
          </Button>
        </Drawer>
      </>
    );
  }

  return (
    <Box
      position="sticky"
      top={stickyTop}
      zIndex={(muiTheme) => muiTheme.zIndex.appBar - 1}
      display="flex"
      gap={2}
      flexWrap="wrap"
      alignItems="center"
      px={2}
      py={1.5}
      bgcolor="background.paper"
      border={1}
      borderColor={(muiTheme) => alpha(muiTheme.palette.text.primary, 0.1)}
      borderRadius={2}
      boxShadow={(muiTheme) => `0 8px 22px ${alpha(muiTheme.palette.common.black, 0.05)}`}
    >
      {children(false)}
      {sortField}
    </Box>
  );
}
